/**
 * ConfirmDialog — the platform's confirmation (owner, 19 Oct 2026: D1, D5).
 *
 * The dialog anatomy (DIALOG_BAR): an icon tile, the title, one line of what
 * happens; what is affected (a `ConsequenceList`, with numbers, never a
 * bulleted list); the way out on the left and the one action on the right.
 *
 * `intent` says how much is at stake, and colours the tile and the action:
 *   - `neutral`  the scope's tone — a step that can be taken back;
 *   - `caution`  amber — reversible, but with a consequence to weigh;
 *   - `danger`   red — ONLY when something is deleted for good.
 *
 * The focus opens on "Cancel" (the safe action), as an alert dialog does: Enter
 * never deletes by accident. While the action runs (`pending`) the dialog
 * stays open, its action says it is working and nothing closes it — the caller
 * closes it when the action has finished, or leaves it open to try again.
 */
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import type { ComponentType, ReactNode } from 'react';

import { DIALOG_BAR, DIALOG_MEDIA, DIALOG_SIZE } from '../lib/dialog-anatomy';
import { useToneScopeClass } from '../lib/tone-scope';
import { cn } from '../lib/utils';
import { AlertDialogOverlay, AlertDialogPortal } from '../primitives/alert-dialog';
import { Button } from '../primitives/button';

export type ConfirmIntent = 'neutral' | 'caution' | 'danger';

const MEDIA_OF: Record<ConfirmIntent, keyof typeof DIALOG_MEDIA> = {
  neutral: 'tone',
  caution: 'caution',
  danger: 'danger',
};

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  intent?: ConfirmIntent;
  icon: ComponentType<{ className?: string }>;
  title: ReactNode;
  description?: ReactNode;
  /** What is affected — a `ConsequenceList`, or a line of text. */
  children?: ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  /** The action is running: the dialog stays, its action shows it is working. */
  pending?: boolean;
  'data-testid'?: string;
  confirmTestId?: string;
  cancelTestId?: string;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  intent = 'neutral',
  icon: Icon,
  title,
  description,
  children,
  confirmLabel,
  cancelLabel,
  onConfirm,
  pending = false,
  'data-testid': testId,
  confirmTestId,
  cancelTestId,
}: ConfirmDialogProps) {
  const scope = useToneScopeClass();
  return (
    <AlertDialogPrimitive.Root
      open={open}
      // Nothing closes it while the action runs.
      onOpenChange={(next) => (pending ? undefined : onOpenChange(next))}
    >
      <AlertDialogPortal>
        <AlertDialogOverlay />
        <AlertDialogPrimitive.Content
          data-slot="confirm-dialog"
          data-intent={intent}
          data-testid={testId}
          className={cn(
            scope,
            'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 flex max-h-[90vh] w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] flex-col overflow-hidden rounded-lg border shadow-lg duration-200',
            DIALOG_SIZE.sm
          )}
        >
          <div className={cn(DIALOG_BAR.header, 'pr-6')}>
            <span
              aria-hidden="true"
              className={cn(
                'flex size-10 shrink-0 items-center justify-center rounded-lg',
                DIALOG_MEDIA[MEDIA_OF[intent]]
              )}
            >
              <Icon className="size-5" />
            </span>
            <div className="min-w-0 flex-1 space-y-1">
              <AlertDialogPrimitive.Title className="text-lg leading-tight font-semibold">
                {title}
              </AlertDialogPrimitive.Title>
              {description ? (
                <AlertDialogPrimitive.Description className="text-muted-foreground text-sm">
                  {description}
                </AlertDialogPrimitive.Description>
              ) : null}
            </div>
          </div>
          {children ? <div className={DIALOG_BAR.body}>{children}</div> : null}
          <div className={DIALOG_BAR.footer}>
            <AlertDialogPrimitive.Cancel asChild>
              <Button type="button" variant="ghost" disabled={pending} data-testid={cancelTestId}>
                {cancelLabel}
              </Button>
            </AlertDialogPrimitive.Cancel>
            <Button
              type="button"
              loading={pending}
              onClick={onConfirm}
              data-testid={confirmTestId}
              {...(intent === 'danger'
                ? { variant: 'destructive' as const }
                : intent === 'caution'
                  ? { tone: 'amber' as const }
                  : {})}
            >
              {confirmLabel}
            </Button>
          </div>
        </AlertDialogPrimitive.Content>
      </AlertDialogPortal>
    </AlertDialogPrimitive.Root>
  );
}

export interface Consequence {
  icon: ComponentType<{ className?: string }>;
  label: ReactNode;
  value?: ReactNode;
}

/** What a confirmation affects: one row each — its icon, what it is, how many. */
export function ConsequenceList({
  items,
  'aria-label': ariaLabel,
  'data-testid': testId,
}: {
  items: readonly Consequence[];
  'aria-label'?: string;
  'data-testid'?: string;
}) {
  return (
    <ul className="divide-y rounded-lg border text-sm" aria-label={ariaLabel} data-testid={testId}>
      {items.map(({ icon: Icon, label, value }, index) => (
        <li
          key={index}
          className="grid grid-cols-[1.25rem_minmax(0,1fr)_auto] items-center gap-3 px-4 py-2.5"
        >
          <Icon className="text-muted-foreground size-4" aria-hidden="true" />
          <span className="text-foreground">{label}</span>
          {value !== undefined ? (
            <span className="text-foreground font-semibold tabular-nums">{value}</span>
          ) : (
            <span />
          )}
        </li>
      ))}
    </ul>
  );
}
